import { Link } from 'react-router-dom';
import { ExternalLink, Shield, Zap, Users, Globe, Lock, Cpu, Heart } from 'lucide-react';
import { SEO } from '../components/SEO';

export const About = () => {
  return (
    <div className="pt-16" data-testid="about-page">
      <SEO 
        title="About Us - Our Mission & Vision"
        description="Turcoaz Apps builds privacy-first online PDF tools and AI document solutions. Learn about our mission to simplify digital workflows with secure, fast, and free productivity tools."
        canonical="/about"
      />

      {/* Hero Section */}
      <section className="py-20 sm:py-24 bg-slate-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-4xl">
            <p 
              className="text-xs tracking-[0.2em] uppercase font-semibold text-teal-700 mb-4"
              style={{ fontFamily: 'Outfit, sans-serif' }}
            >
              About Turcoaz Apps
            </p>
            <h1 
              className="text-4xl sm:text-5xl tracking-tight leading-tight font-semibold text-slate-900 mb-6"
              style={{ fontFamily: 'Outfit, sans-serif' }}
            >
              Building Software That Respects Your Time and Privacy
            </h1>
            <p className="text-xl text-slate-600 leading-relaxed">
              We're a modern software company on a mission to make document workflows simpler, faster, and more secure. In a world cluttered with bloated software and privacy concerns, we build tools that just work—without compromises.
            </p>
          </div>
        </div>
      </section>

      {/* Introduction */}
      <section className="py-16 sm:py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-4xl">
            <h2 
              className="text-2xl sm:text-3xl tracking-tight font-medium text-slate-900 mb-6"
              style={{ fontFamily: 'Outfit, sans-serif' }}
            >
              Who We Are
            </h2>
            <div className="space-y-4 text-slate-600 leading-relaxed">
              <p>
                Turcoaz Apps is a software company focused on creating practical, accessible tools for everyday digital tasks. We specialize in <strong>online PDF tools</strong>, <strong>AI document solutions</strong>, and productivity software designed to help individuals and businesses work more efficiently.
              </p>
              <p>
                Our journey began with a simple observation: most document tools are either overly complicated, expensive, or raise serious privacy concerns. We set out to change that by building software that combines professional-grade functionality with an interface anyone can use—no technical expertise required.
              </p>
              <p>
                Today, our flagship product, <a href="https://pdf.turcoazapps.com" target="_blank" rel="noopener noreferrer" className="text-teal-600 hover:text-teal-700 font-medium">Smart PDF Tools</a>, serves users worldwide who need reliable, <strong>secure document processing</strong> without the hassle of desktop software installations or subscription fees for basic features.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Mission Section */}
      <section className="py-16 sm:py-20 bg-slate-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-4xl">
            <h2 
              className="text-2xl sm:text-3xl tracking-tight font-medium text-slate-900 mb-6"
              style={{ fontFamily: 'Outfit, sans-serif' }}
            >
              Our Mission
            </h2>
            <div className="space-y-4 text-slate-600 leading-relaxed">
              <p>
                Our mission is straightforward: <strong>simplify document workflows and digital tasks</strong> for everyone. We believe that powerful software shouldn't require a steep learning curve or empty your wallet.
              </p>
              <p>
                Every feature we build is guided by three principles:
              </p>
              <ul className="space-y-3 my-6">
                <li className="flex items-start gap-3">
                  <div className="w-6 h-6 rounded-full bg-teal-100 flex items-center justify-center mt-0.5 flex-shrink-0">
                    <div className="w-2 h-2 rounded-full bg-teal-600" />
                  </div>
                  <span><strong>Simplicity first:</strong> If a feature adds complexity without clear value, we don't ship it.</span>
                </li>
                <li className="flex items-start gap-3">
                  <div className="w-6 h-6 rounded-full bg-teal-100 flex items-center justify-center mt-0.5 flex-shrink-0">
                    <div className="w-2 h-2 rounded-full bg-teal-600" />
                  </div>
                  <span><strong>Privacy by design:</strong> Your files are yours. We process them, then delete them—no exceptions.</span>
                </li>
                <li className="flex items-start gap-3">
                  <div className="w-6 h-6 rounded-full bg-teal-100 flex items-center justify-center mt-0.5 flex-shrink-0">
                    <div className="w-2 h-2 rounded-full bg-teal-600" />
                  </div>
                  <span><strong>Accessible to all:</strong> Core tools remain free because productivity shouldn't be a privilege.</span>
                </li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* Vision Section */}
      <section className="py-16 sm:py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-4xl">
            <h2 
              className="text-2xl sm:text-3xl tracking-tight font-medium text-slate-900 mb-6"
              style={{ fontFamily: 'Outfit, sans-serif' }}
            >
              Our Vision
            </h2>
            <div className="space-y-4 text-slate-600 leading-relaxed">
              <p>
                We envision a future where digital tools empower rather than overwhelm. Where a student in any country can access the same quality <strong>free PDF tools</strong> as a Fortune 500 company. Where privacy isn't a premium feature but a baseline expectation.
              </p>
              <p>
                As AI transforms how we work with documents, we're committed to harnessing these technologies responsibly. Our <strong>AI document tools</strong> are designed to augment human capability—helping you work faster and smarter while keeping you in control of your data.
              </p>
              <p>
                We're not trying to build the biggest software company. We're focused on building the most trusted one in our space. Growth means nothing if it comes at the cost of the principles that define us.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Why Choose Us */}
      <section className="py-16 sm:py-20 bg-slate-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-4xl mb-12">
            <h2 
              className="text-2xl sm:text-3xl tracking-tight font-medium text-slate-900 mb-6"
              style={{ fontFamily: 'Outfit, sans-serif' }}
            >
              Why Choose Turcoaz Apps
            </h2>
            <p className="text-slate-600 leading-relaxed">
              In a market crowded with document tools, here's what sets us apart:
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 max-w-4xl">
            <div className="bg-white border border-slate-200/60 rounded-xl p-6">
              <div className="w-10 h-10 bg-teal-50 rounded-lg flex items-center justify-center mb-4">
                <Lock className="w-5 h-5 text-teal-600" />
              </div>
              <h3 
                className="text-lg font-medium text-slate-900 mb-2"
                style={{ fontFamily: 'Outfit, sans-serif' }}
              >
                Privacy-First Architecture
              </h3>
              <p className="text-slate-600 text-sm leading-relaxed">
                Your files are processed in isolated environments and automatically deleted after completion. We don't store, analyze, or monetize your documents. Ever.
              </p>
            </div>

            <div className="bg-white border border-slate-200/60 rounded-xl p-6">
              <div className="w-10 h-10 bg-teal-50 rounded-lg flex items-center justify-center mb-4">
                <Zap className="w-5 h-5 text-teal-600" />
              </div>
              <h3 
                className="text-lg font-medium text-slate-900 mb-2"
                style={{ fontFamily: 'Outfit, sans-serif' }}
              >
                Speed Without Sacrifice
              </h3>
              <p className="text-slate-600 text-sm leading-relaxed">
                Our tools are optimized for performance. Merge, split, or compress PDFs in seconds—not minutes. No waiting, no spinning wheels.
              </p>
            </div>

            <div className="bg-white border border-slate-200/60 rounded-xl p-6">
              <div className="w-10 h-10 bg-teal-50 rounded-lg flex items-center justify-center mb-4">
                <Heart className="w-5 h-5 text-teal-600" />
              </div>
              <h3 
                className="text-lg font-medium text-slate-900 mb-2"
                style={{ fontFamily: 'Outfit, sans-serif' }}
              >
                Genuinely Free Tools
              </h3>
              <p className="text-slate-600 text-sm leading-relaxed">
                We offer free PDF tools that actually work—no watermarks on basic operations, no artificial limitations designed to frustrate you into upgrading.
              </p>
            </div>

            <div className="bg-white border border-slate-200/60 rounded-xl p-6">
              <div className="w-10 h-10 bg-teal-50 rounded-lg flex items-center justify-center mb-4">
                <Globe className="w-5 h-5 text-teal-600" />
              </div>
              <h3 
                className="text-lg font-medium text-slate-900 mb-2"
                style={{ fontFamily: 'Outfit, sans-serif' }}
              >
                No Installation Required
              </h3>
              <p className="text-slate-600 text-sm leading-relaxed">
                Everything runs in your browser. No downloads, no updates, no compatibility issues. Works on any device with an internet connection.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Technology Section */}
      <section className="py-16 sm:py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-4xl">
            <h2 
              className="text-2xl sm:text-3xl tracking-tight font-medium text-slate-900 mb-6"
              style={{ fontFamily: 'Outfit, sans-serif' }}
            >
              Our Technology
            </h2>
            <div className="space-y-4 text-slate-600 leading-relaxed">
              <p>
                Behind our simple interfaces lies sophisticated technology designed for reliability and security. We've built our infrastructure with modern cloud architecture that scales automatically based on demand—ensuring consistent performance whether you're processing one document or one hundred.
              </p>
              <p>
                Our document processing engines are purpose-built for specific tasks. When you use our <strong>online PDF tools</strong>, your files are handled by specialized processors that understand PDF structure intimately. This allows us to deliver features like lossless compression and precise page manipulation that generic tools simply can't match.
              </p>
              <p>
                Security isn't an afterthought—it's woven into every layer. All file transfers use TLS encryption. Processing happens in isolated containers that are destroyed after each operation. We maintain no persistent storage of user documents, and our systems are regularly audited for vulnerabilities.
              </p>
              <p>
                As we expand into <strong>AI-powered document tools</strong>, we're applying the same principles. AI features run on secure infrastructure with strict data isolation. Your documents are never used to train models or shared with third parties.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Who We Build For */}
      <section className="py-16 sm:py-20 bg-slate-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-4xl">
            <h2 
              className="text-2xl sm:text-3xl tracking-tight font-medium text-slate-900 mb-6"
              style={{ fontFamily: 'Outfit, sans-serif' }}
            >
              Who We Build For
            </h2>
            <div className="space-y-4 text-slate-600 leading-relaxed mb-8">
              <p>
                Our tools serve a diverse community of users who share one thing in common: they need to work with documents efficiently and securely.
              </p>
            </div>
            
            <div className="space-y-6">
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 bg-teal-50 rounded-lg flex items-center justify-center flex-shrink-0">
                  <Users className="w-6 h-6 text-teal-600" />
                </div>
                <div>
                  <h3 
                    className="text-lg font-medium text-slate-900 mb-1"
                    style={{ fontFamily: 'Outfit, sans-serif' }}
                  >
                    Students and Educators
                  </h3>
                  <p className="text-slate-600">
                    From combining research papers to compressing large assignments for email submission, students rely on our free tools daily. Educators use them to prepare materials and manage coursework documents without budget constraints.
                  </p>
                </div>
              </div>

              <div className="flex items-start gap-4">
                <div className="w-12 h-12 bg-teal-50 rounded-lg flex items-center justify-center flex-shrink-0">
                  <Shield className="w-6 h-6 text-teal-600" />
                </div>
                <div>
                  <h3 
                    className="text-lg font-medium text-slate-900 mb-1"
                    style={{ fontFamily: 'Outfit, sans-serif' }}
                  >
                    Professionals and Freelancers
                  </h3>
                  <p className="text-slate-600">
                    Lawyers, accountants, consultants, and freelancers handle sensitive documents regularly. They choose us because our privacy-first approach means client confidentiality is never compromised. No accounts required, no data retained.
                  </p>
                </div>
              </div>

              <div className="flex items-start gap-4">
                <div className="w-12 h-12 bg-teal-50 rounded-lg flex items-center justify-center flex-shrink-0">
                  <Cpu className="w-6 h-6 text-teal-600" />
                </div>
                <div>
                  <h3 
                    className="text-lg font-medium text-slate-900 mb-1"
                    style={{ fontFamily: 'Outfit, sans-serif' }}
                  >
                    Small Businesses and Teams
                  </h3>
                  <p className="text-slate-600">
                    Small businesses need professional document capabilities without enterprise software costs. Our tools provide the functionality teams need for contracts, proposals, and reports—without per-seat licensing or complex deployments.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Contact & Transparency */}
      <section className="py-16 sm:py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-4xl">
            <h2 
              className="text-2xl sm:text-3xl tracking-tight font-medium text-slate-900 mb-6"
              style={{ fontFamily: 'Outfit, sans-serif' }}
            >
              Contact & Transparency
            </h2>
            <div className="space-y-4 text-slate-600 leading-relaxed">
              <p>
                We believe in operating transparently. Our <Link to="/privacy-policy" className="text-teal-600 hover:text-teal-700 font-medium">privacy policy</Link> and <Link to="/terms" className="text-teal-600 hover:text-teal-700 font-medium">terms of service</Link> are written in plain language—not legal jargon designed to obscure what we actually do with your data (spoiler: we don't keep it).
              </p>
              <p>
                We're always interested in hearing from users. Whether you've found a bug, have a feature suggestion, or just want to share how you're using our tools, we read every message that comes through our <Link to="/contact" className="text-teal-600 hover:text-teal-700 font-medium">contact form</Link>.
              </p>
              <p>
                Real people answer real questions. We don't hide behind chatbots or endless support ticket queues. When you reach out, you'll hear back from someone who can actually help.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Closing CTA */}
      <section className="py-16 sm:py-20 bg-slate-900">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 
            className="text-2xl sm:text-3xl tracking-tight font-medium text-white mb-4"
            style={{ fontFamily: 'Outfit, sans-serif' }}
          >
            Ready to Experience the Difference?
          </h2>
          <p className="text-lg text-slate-300 mb-8 max-w-2xl mx-auto">
            Try Smart PDF Tools today. No signup required, no credit card needed. Just fast, secure document processing that respects your privacy.
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
            <a
              href="https://pdf.turcoazapps.com"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 bg-white text-slate-900 px-8 py-3.5 rounded-md text-base font-medium hover:bg-slate-100 transition-colors"
              data-testid="about-cta-button"
            >
              Try Smart PDF Tools
              <ExternalLink className="w-5 h-5" />
            </a>
            <Link
              to="/contact"
              className="inline-flex items-center gap-2 bg-transparent text-white border border-white/30 px-8 py-3.5 rounded-md text-base font-medium hover:bg-white/10 transition-colors"
              data-testid="about-contact-button"
            >
              Get in Touch
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};
